// Resolves a minyan's dynamic-zman time to a real clock time for the Minyanim
// Roll editor's live preview, computed from the organization's location — so the
// preview matches what the live display shows, including the +/- offset.
//
// kosher-zmanim is loaded lazily (and cached) so it only ships when an editor
// that needs it is opened.

import { markRaw } from 'vue';
import { ZMANIM_ENGLISH_OFFSETS } from '@/utils/const';
import {
  MOTZEI_SHABBOS_LONDON_KEY,
  resolveMotzeiShabbosLondonFrom,
} from '@/utils/motzei_shabbos_london';
import { isSpecialZmanKey, resolveSpecialZmanIsoFromOverrides } from '@/utils/special_zmanim';
import { getZonedDateTimeParts, toLocalNoon } from '@/utils/display_preview';
import { getWeeklyZmanWeekDates } from '@/utils/minyanRollEngine';

let kosherZmanimPromise: Promise<any> | null = null;
function loadKosherZmanim(): Promise<any> {
  if (!kosherZmanimPromise) kosherZmanimPromise = import('kosher-zmanim');
  return kosherZmanimPromise;
}

export interface PreviewOrgLocation {
  latitude?: number | string | null;
  longitude?: number | string | null;
  timezone?: string | null;
  name?: string | null;
}

export interface PreviewZmanimContext {
  calendar: any;
  timeZone: string;
  /**
   * Settings → Zmanim Settings → Special Zmanim rows (Zmanim_Display_Override_N).
   * When the org configured Hadlukas Haneiros / Motzei there, a minyan whose
   * Dynamic Zman selects one of those keys resolves from these settings — exactly
   * as the live display does.
   */
  specialZmanimOverrides?: any[];
}

// Build a kosher-zmanim calendar for the organization's coordinates. The
// calendar defaults to today (matching the live display). Returns null when
// there are no usable coordinates. `specialZmanimOverrides` are the org's saved
// Special Zmanim settings, used to resolve candle lighting / motzei.
//
// The context is marked raw: the editors keep it in `data`, and Vue must never
// proxy the live calendar inside it. The Weekly Static Zman scan moves that
// calendar's date and restores it; through a reactive proxy each of those
// writes re-queued the editor's render from inside its own render, which a
// production build never stops (the tab froze). Replacing the whole context
// still refreshes every preview that reads it.
export async function buildPreviewZmanimContext(
  org: PreviewOrgLocation | null | undefined,
  specialZmanimOverrides?: any[] | null,
): Promise<PreviewZmanimContext | null> {
  const lat = Number(org?.latitude);
  const lng = Number(org?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const timeZone =
    (typeof org?.timezone === 'string' && org.timezone.trim()) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'UTC';

  try {
    const { ComplexZmanimCalendar, GeoLocation } = await loadKosherZmanim();
    const geo = new GeoLocation(org?.name || `${lat}, ${lng}`, lat, lng, 0, timeZone);
    const calendar = new ComplexZmanimCalendar(geo);
    return markRaw({
      calendar,
      timeZone,
      specialZmanimOverrides: Array.isArray(specialZmanimOverrides) ? specialZmanimOverrides : [],
    });
  } catch (e) {
    return null;
  }
}

function valueToIso(v: any): string {
  if (!v) return '';
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? '' : v.toISOString();
  if (typeof v.toISO === 'function' && v.isValid !== false) return v.toISO() || '';
  if (typeof v.toJSDate === 'function') {
    const d = v.toJSDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : '';
  }
  return '';
}

// Call the matching `get<Key>()` getter on the calendar (e.g. sofZmanTfilaGRA →
// getSofZmanTfilaGRA), returning an ISO string. `anyArity` also calls a getter
// whose parameters are all optional (getPlagHamincha, getMinchaGedola,
// getMinchaKetana), as the live display's Weekly Static Zman scan does; one
// that needs an argument throws there and resolves to '' here as well.
function callGetter(cal: any, key: string, anyArity = false): string {
  if (!cal || !key) return '';
  const getter = `get${key.charAt(0).toUpperCase()}${key.slice(1)}`;
  const fn = cal[getter];
  if (typeof fn === 'function' && (anyArity || fn.length === 0)) {
    try {
      return valueToIso(fn.call(cal));
    } catch (e) {
      return '';
    }
  }
  return '';
}

function addMinutesIso(iso: string, minutes: number): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const m = Number(minutes);
  return new Date(d.getTime() + (Number.isFinite(m) ? m : 0) * 60000).toISOString();
}

// Resolve a minyan zman key to an absolute ISO time (before the offset).
// `anyArity` is the live display's Weekly Static Zman getter rule (see
// callGetter), so the weekly preview resolves every day exactly as it does.
function resolveZmanBaseIso(ctx: PreviewZmanimContext, zmanimKey: string, anyArity = false): string {
  const cal = ctx.calendar;
  const get = (key: string) => callGetter(cal, key, anyArity);
  // Hadlukas Haneiros / Motzei come from the org-wide Settings → Special Zmanim
  // configuration when it is set, matching the live display. When it isn't
  // configured this returns '' and the normal resolution below runs unchanged.
  if (isSpecialZmanKey(zmanimKey)) {
    const sunsetIso = get('sunset') || get('seaLevelSunset');
    const specialIso = resolveSpecialZmanIsoFromOverrides(ctx.specialZmanimOverrides, zmanimKey, {
      sunsetIso,
      calendar: cal,
    });
    if (specialIso) return specialIso;
  }

  const direct = get(zmanimKey);
  if (direct) return direct;
  // A couple of editor-offered keys have no direct kosher-zmanim getter.
  if (zmanimKey === 'chatzotNight') {
    const chatzos = get('chatzos');
    return chatzos ? addMinutesIso(chatzos, 12 * 60) : '';
  }
  if (zmanimKey === 'misheyakir22Min') {
    const alos72 = get('alos72');
    return alos72 ? addMinutesIso(alos72, 22) : '';
  }
  if (zmanimKey === MOTZEI_SHABBOS_LONDON_KEY) {
    // Tzeis 8.5° vs Tzeis 72 (R"T), picked by the shared three-case rule.
    return resolveMotzeiShabbosLondonFrom((baseKey) => get(baseKey));
  }
  // Legacy keys (e.g. `sofZmanTfillaMGA`) have no matching kosher-zmanim getter.
  // The live display resolves these to a sunrise + fixed-offset estimate
  // (getComputedZmanDateTime / ZMANIM_ENGLISH_OFFSETS); mirror that here so the
  // preview shows the same clock time instead of falling back to the raw label.
  const baseOffset = Number((ZMANIM_ENGLISH_OFFSETS as Record<string, number>)[zmanimKey]);
  if (Number.isFinite(baseOffset)) {
    const sunrise = get('sunrise');
    if (sunrise) return addMinutesIso(sunrise, baseOffset);
  }
  return '';
}

// Resolve a minyan zman key + offset to an ISO datetime, or '' when it can't.
export function resolveMinyanZmanIso(
  ctx: PreviewZmanimContext | null | undefined,
  zmanimKey: string,
  offsetMinutes: number = 0,
): string {
  if (!ctx?.calendar || !zmanimKey) return '';
  const base = resolveZmanBaseIso(ctx, zmanimKey);
  if (!base) return '';
  const offset = Number(offsetMinutes);
  return Number.isFinite(offset) && offset !== 0 ? addMinutesIso(base, offset) : base;
}

// --- Weekly Static Zman (editor live preview) ---
// Resolve a zman for EVERY day of the current week (Sunday through Friday) and
// return the ISO of the maximum ('max') or minimum ('min') clock time — the one
// static time such a minyan shows all week. Mirrors the live display's
// resolveWeeklyStaticZmanDateTime so the preview matches it exactly.
export function resolveWeeklyStaticZmanIso(
  ctx: PreviewZmanimContext | null | undefined,
  zmanimKey: string,
  weeklyMode: string = 'max',
): string {
  const cal = ctx?.calendar;
  if (!cal || !zmanimKey) return '';

  // Today as the organization's calendar reads it — the live display's
  // getScreenCivilDate — so the week turns over at the org's Sunday midnight,
  // not the browser's. Local noon avoids DST / midnight date-boundary ambiguity
  // when the calendar reads the date's Y/M/D.
  const now = new Date();
  const parts = getZonedDateTimeParts(now, ctx?.timeZone || '');
  let today: Date;
  if (parts) {
    today = toLocalNoon(parts);
  } else {
    today = new Date(now.getTime());
    today.setHours(12, 0, 0, 0);
  }

  const pickMin = weeklyMode === 'min';
  let original: any = null;
  try {
    original = cal.getDate();
  } catch (e) {
    original = null;
  }

  let bestIso = '';
  let bestMinutes = Number.NaN;
  try {
    // Sunday through Friday of today's week (Shabbos still reads the week
    // that began the Sunday before), the dates the live display scans.
    for (const date of getWeeklyZmanWeekDates(today)) {
      let iso = '';
      try {
        cal.setDate(date);
        iso = resolveZmanBaseIso(ctx as PreviewZmanimContext, zmanimKey, true);
      } catch (e) {
        iso = '';
      }
      if (!iso) continue;
      // Compare by clock time in the org timezone (the shown value), not by
      // absolute instant — "latest/earliest time of the week" is a wall-clock
      // comparison across the six days.
      const minutes = zmanIsoToMinutesOfDay(iso, ctx?.timeZone);
      if (!Number.isFinite(minutes)) continue;
      if (!bestIso || (pickMin ? minutes < bestMinutes : minutes > bestMinutes)) {
        bestIso = iso;
        bestMinutes = minutes;
      }
    }
  } finally {
    // Restore the live date so later resolutions off the shared calendar
    // (today's dynamic zmanim) keep operating on today.
    if (original != null) {
      try {
        cal.setDate(original);
      } catch (e) {
        /* noop */
      }
    }
  }
  return bestIso;
}

// "h:mm" in the organization's timezone, 12h, no AM/PM (matches the display).
export function formatZmanIsoToClock(iso: string, timeZone?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d
    .toLocaleTimeString('en-US', {
      timeZone: timeZone || undefined,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .replace(/\s*(AM|PM)$/i, '')
    .trim();
}

// Minutes-of-day in the organization's timezone (for sort / highlight / countdown).
export function zmanIsoToMinutesOfDay(iso: string, timeZone?: string): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return Number.POSITIVE_INFINITY;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || undefined,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(d);
    const hour = Number(parts.find((p) => p.type === 'hour')?.value);
    const minute = Number(parts.find((p) => p.type === 'minute')?.value);
    if (Number.isFinite(hour) && Number.isFinite(minute)) {
      return (hour % 24) * 60 + minute;
    }
  } catch (e) {
    /* fall through to local time */
  }
  return d.getHours() * 60 + d.getMinutes();
}
